package dev.krno.reveal

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import dev.krno.rive.__krno
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.NodeList
import org.w3c.dom.events.Event
import kotlin.math.min

private const val SELECTOR =
    ".head,.slot,.spk,.card,.venue,.plist,.plogo,.cta,.hero-copy,.hero-art"

/**
 * Ports the reveal behaviour from `krno.js` line 26–34.
 *
 * Additive-only: whatever happens, every target must reach `.in` within 1.4 s of
 * first render — including in embedded/non-scrolling contexts. That's what the
 * unconditional `setTimeout(showAll, 1400)` guarantees, per README §Scroll reveal.
 */
@Composable
fun RevealController() {
    DisposableEffect(Unit) {
        val targets: MutableList<HTMLElement> = mutableListOf()
        val nodes: NodeList = document.querySelectorAll(SELECTOR)
        for (i in 0 until nodes.length) {
            val el = nodes.item(i) as? HTMLElement ?: continue
            el.classList.add("rv")
            el.style.transitionDelay = "${min((i % 8) * 45, 320)}ms"
            targets += el
        }

        fun reveal() {
            val vh = window.innerHeight.takeIf { it > 0 } ?: 900
            val cutoff = vh * 0.94
            val it = targets.listIterator()
            while (it.hasNext()) {
                val el = it.next()
                if (el.getBoundingClientRect().top < cutoff) {
                    el.classList.add("in")
                    it.remove()
                }
            }
        }

        fun showAll() {
            targets.forEach { it.classList.add("in") }
            targets.clear()
        }

        val scrollListener: (Event) -> Unit = { reveal() }
        val resizeListener: (Event) -> Unit = { reveal() }
        val loadListener: (Event) -> Unit = { reveal() }
        window.addEventListener("scroll", scrollListener)
        window.addEventListener("resize", resizeListener)
        window.addEventListener("load", loadListener)

        window.requestAnimationFrame {
            reveal()
            window.requestAnimationFrame { reveal() }
        }

        val fonts = document.asDynamic().fonts
        if (fonts != null && fonts.ready != null) {
            (fonts.ready as? kotlin.js.Promise<*>)?.then { reveal() }
        }

        val options: dynamic = js("({})")
        options.rootMargin = "0px 0px -6% 0px"
        val observer = __krno.newIntersectionObserver({ entries: dynamic, obs: dynamic ->
            val n = entries.length as Int
            for (i in 0 until n) {
                val entry = entries[i]
                if (entry.isIntersecting == true) {
                    (entry.target as HTMLElement).classList.add("in")
                    obs.unobserve(entry.target)
                }
            }
        }, options)
        targets.forEach { el -> observer.observe(el) }

        val timer600 = window.setTimeout({ reveal() }, 600)
        val timer1400 = window.setTimeout({ showAll() }, 1400)

        onDispose {
            window.removeEventListener("scroll", scrollListener)
            window.removeEventListener("resize", resizeListener)
            window.removeEventListener("load", loadListener)
            window.clearTimeout(timer600)
            window.clearTimeout(timer1400)
            observer.disconnect()
        }
    }
}
